import type { FilterDefinition, FilterContext } from '../types';

// These should match your constants file values
// Import them from your actual constants in production:
// import { RECTANGLE_BAR, ROUND_BAR, ... } from '../../config/constants';

// For reference, these are the shape display_name values used in visibility conditions.
// The actual IDs will be resolved at runtime from the shapes prop.
const RECT_SHAPES = ['Rectangle Bar', 'Rectangle Tube', 'Plate', 'Angle', 'Channel', 'Hex Bar', 'Extrusion'];
const RECT_NO_HEX = ['Rectangle Bar', 'Rectangle Tube', 'Plate', 'Angle', 'Channel', 'Extrusion'];
const ROUND_SHAPES = ['Round Bar', 'Round Tube'];
const WALL_SHAPES = ['Angle', 'Channel', 'Rectangle Tube', 'Round Tube'];

/**
 * Creates filter definitions.
 *
 * We need a factory function because visibility conditions depend on
 * shape IDs which are dynamic (come from the API). The `shapeIdMap`
 * parameter maps display_name -> id so we can build visibility rules.
 *
 * @param shapeIdMap - Maps shape display_name to shape id
 */
export function createFilterDefinitions(
	shapeIdMap: Record<string, string>
): FilterDefinition[] {
	// Helper: resolve display names to IDs for visibility conditions
	const resolveShapeIds = (displayNames: string[]): string[] =>
		displayNames.map((name) => shapeIdMap[name]).filter(Boolean);

	return [
		// ── Building ─────────────────────────────────────────────────────────
		{
			key: 'building',
			type: 'select',
			label: 'Building',
			contexts: ['desktop', 'tablet'] as FilterContext[],
			urlKeys: { value: 'building' },
			optionsFromProp: 'buildings',
			placeholder: 'All Buildings',
			mode: 'multiple',
			resettable: true,
			requireApply: false,
		},

		// ── ID Search Filters ────────────────────────────────────────────────
		{
			key: 'purchase_order_number',
			type: 'search-input',
			label: 'PO Number',
			contexts: ['desktop', 'tablet'] as FilterContext[],
			urlKeys: { value: 'purchase_order_number' },
			resettable: true,
			placeholder: 'Search by PO Number',
		},
		{
			key: 'length_friendly_id',
			type: 'search-input',
			label: 'Length ID',
			contexts: ['desktop'] as FilterContext[],
			urlKeys: { value: 'length_friendly_id' },
			resettable: true,
			placeholder: 'Search by Length ID',
		},

		// ── Shape ────────────────────────────────────────────────────────────
		{
			key: 'shape',
			type: 'checkbox',
			label: 'Shape',
			contexts: ['desktop', 'tablet'] as FilterContext[],
			urlKeys: { value: 'shape' },
			optionsFromProp: 'shapes',
		},

		// ── Category ─────────────────────────────────────────────────────────
		{
			key: 'category',
			type: 'radio',
			label: 'Category',
			contexts: ['desktop', 'tablet'] as FilterContext[],
			urlKeys: { value: 'category' },
			optionsFromProp: 'categories',
			resettable: true,
		},

		// ── Type ─────────────────────────────────────────────────────────────
		{
			key: 'types',
			type: 'checkbox',
			label: 'Type',
			contexts: ['desktop', 'tablet'] as FilterContext[],
			urlKeys: { value: 'types' },
			optionsFromProp: 'types',
			searchable: true,
			dependsOn: 'category',
		},

		// ── Location ─────────────────────────────────────────────────────────
		{
			key: 'inventory_location',
			type: 'select',
			label: 'Location',
			contexts: ['desktop', 'tablet'] as FilterContext[],
			urlKeys: { value: 'inventory_location' },
			mode: 'multiple',
			placeholder: 'Select location',
			optionGroupsFromProp: 'locations',
			requireApply: true,
			resettable: true,
		},

		// ── Status ───────────────────────────────────────────────────────────
		{
			key: 'status',
			type: 'checkbox',
			label: 'Status',
			contexts: ['desktop', 'tablet'] as FilterContext[],
			urlKeys: { value: 'status' },
			options: [
				{ value: 'In Stock', label: 'In Stock' },
				{ value: 'Awaiting Delivery', label: 'Awaiting Delivery' },
				{ value: 'Quality Hold', label: 'Quality Hold' },
				{ value: 'Scrapped', label: 'Scrapped' },
			],
		},

		// ── Dimensional Filters (Range) ──────────────────────────────────────

		{
			key: 'height',
			type: 'range',
			label: 'Height',
			contexts: ['desktop', 'tablet'] as FilterContext[],
			urlKeys: { min: 'height_min', max: 'height_max' },
			initialRangeKey: 'heightRange',
			step: 0.25,
			suffix: 'in',
			visibleWhen: { shape: resolveShapeIds(RECT_SHAPES) },
		},
		{
			key: 'width',
			type: 'range',
			label: 'Width',
			contexts: ['desktop', 'tablet'] as FilterContext[],
			urlKeys: { min: 'width_min', max: 'width_max' },
			initialRangeKey: 'widthRange',
			step: 0.25,
			suffix: 'in',
			visibleWhen: { shape: resolveShapeIds(RECT_NO_HEX) },
		},
		{
			key: 'outer_diameter',
			type: 'range',
			label: 'Outer Diameter',
			contexts: ['desktop', 'tablet'] as FilterContext[],
			urlKeys: { min: 'outer_diameter_min', max: 'outer_diameter_max' },
			initialRangeKey: 'outerDiameterRange',
			step: 0.25,
			suffix: 'in',
			visibleWhen: { shape: resolveShapeIds(ROUND_SHAPES) },
		},
		{
			key: 'inner_diameter',
			type: 'range',
			label: 'Inner Diameter',
			contexts: ['desktop', 'tablet'] as FilterContext[],
			urlKeys: { min: 'inner_diameter_min', max: 'inner_diameter_max' },
			initialRangeKey: 'innerDiameterRange',
			step: 0.25,
			suffix: 'in',
			visibleWhen: { shape: resolveShapeIds(['Round Tube']) },
		},
		{
			key: 'wall_thickness',
			type: 'range',
			label: 'Wall Thickness',
			contexts: ['desktop', 'tablet'] as FilterContext[],
			urlKeys: { min: 'wall_min', max: 'wall_max' },
			initialRangeKey: 'wallThicknessRange',
			step: 0.25,
			suffix: 'in',
			visibleWhen: { shape: resolveShapeIds(WALL_SHAPES) },
		},
		{
			key: 'current_length',
			type: 'range',
			label: 'Current Length',
			contexts: ['desktop', 'tablet'] as FilterContext[],
			urlKeys: { min: 'current_length_min', max: 'current_length_max' },
			initialRangeKey: 'currentLengthRange',
			step: 0.25,
			suffix: 'in',
		},
		{
			key: 'available_length',
			type: 'range',
			label: 'Available Length',
			contexts: ['desktop', 'tablet'] as FilterContext[],
			urlKeys: { min: 'min_total_available_length', max: 'max_total_available_length' },
			initialRangeKey: 'availableLengthRange',
			step: 0.125,
			suffix: 'in',
		},

		// ── Certificate ──────────────────────────────────────────────────────
		{
			key: 'has_certificate',
			type: 'radio',
			label: 'Has Certificate',
			contexts: ['desktop', 'tablet'] as FilterContext[],
			urlKeys: { value: 'has_certificate' },
			options: [
				{ value: 'true', label: 'Yes (Verified)' },
				{ value: 'false', label: 'No Certificate' },
			],
			showAllOption: true,
			allOptionLabel: 'Show All',
		},
	];
}